// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { IFailoverDomainResponse } from 'common/api/resources/FailoverDomain';
import {
    Switch,
    Translate,
} from '@plesk/ui-library';
import List from 'common/components/List/List';
import {
    FAILOVER_DOMAIN,
    FAILOVER_DOMAINS,
} from 'admin/failoverDomain/constants/tests';
import { EmptyView } from 'common/components/EmptyView/EmptyView';
import { ActionsCell } from 'common/components/ActionsCell/ActionsCell';
import { dataCySelector } from 'common/tests/selectors';
import { ICONS } from 'common/constants';
import { GreenSwitch } from 'admin/failoverDomain/containers/Styles';

const columns = [{
    width: '5%',
    key: 'colId',
    title: <Translate content="failoverDomain.list.id" />,
}, {
    key: 'colName',
    cellProps: {
        className: 'cell-bold',
    },
    title: <Translate content="failoverDomain.list.nameDescription" />,
}, {
    width: '15%',
    key: 'colStorage',
    title: <Translate content="failoverDomain.list.storage" />,
}, {
    width: '10%',
    key: 'colCrs',
    title: <Translate content="failoverDomain.list.computeResources" />,
}, {
    width: '12%',
    key: 'colLocation',
    title: <Translate content="failoverDomain.list.location" />,
}, {
    width: '10%',
    key: 'colIpAddresses',
    title: <Translate content="failoverDomain.list.ipAddresses" />,
}, {
    width: '1%',
    key: 'colIsActive',
    title: <Translate content="failoverDomain.list.active" />,
}, {
    width: '1%',
    key: 'colActions',
    title: '',
}];

export interface IFailoverDomainTableProps {
    items: IFailoverDomainResponse[];
    onCreate: () => void;
    onEdit: (item: IFailoverDomainResponse) => void;
    onToggleActive: (item: IFailoverDomainResponse) => void;
    onDelete: (item: IFailoverDomainResponse) => void;
    isLoading?: boolean;
}

export const FailoverDomainTable: React.FC<IFailoverDomainTableProps> = ({
    items,
    onCreate,
    onEdit,
    onToggleActive,
    onDelete,
    isLoading,
}) => {
    const data = items.map((item) => ({
        colId: item.id,
        colName: (
            <div>
                {item.name}
                {item.description && (
                    <div className="text-muted" style={{ fontSize: '12px' }}>
                        {item.description}
                    </div>
                )}
            </div>
        ),
        colStorage: item.storage.name,
        colCrs: item.total_compute_resources_count,
        colLocation: (
            <span>
                {item.location.icon && (
                    <img
                        src={item.location.icon.url}
                        alt={item.location.name}
                        style={{ width: 20, height: 14, marginRight: 8, verticalAlign: 'middle' }}
                    />
                )}
                {item.location.name}
            </span>
        ),
        colIpAddresses: `${item.ip_addresses.used}/${item.ip_addresses.total}`,
        colIsActive: (
            <GreenSwitch>
                <Switch
                    checked={item.is_active}
                    onChange={() => onToggleActive(item)}
                    loading={item.is_loading}
                    data-cy={dataCySelector(item.id, FAILOVER_DOMAIN.IS_ACTIVE)}
                />
            </GreenSwitch>
        ),
        colActions: (
            <ActionsCell
                actions={[
                    {
                        icon: ICONS.GEAR,
                        label: <Translate content="failoverDomain.editButton" />,
                        onClick: () => onEdit(item),
                        'data-cy': dataCySelector(item.id, FAILOVER_DOMAIN.EDIT),
                    },
                    {
                        icon: ICONS.RECYCLE,
                        label: <Translate content="failoverDomain.deleteConfirmation.button" />,
                        confirmation: {
                            isLoading: item.is_loading,
                            button: <Translate content="failoverDomain.deleteConfirmation.button" />,
                            title: <Translate content="failoverDomain.deleteConfirmation.title" />,
                            tooltip: <Translate content="failoverDomain.deleteConfirmation.tooltip" />,
                            handleConfirm: () => onDelete(item),
                        },
                        'data-cy': dataCySelector(item.id, FAILOVER_DOMAIN.DELETE),
                    },
                ]}
            />
        ),
        key: item.id.toString(),
    }));

    return (
        <List
            emptyView={
                <EmptyView
                    title="failoverDomain.emptyView.title"
                    description="failoverDomain.emptyView.description"
                    buttonText="failoverDomain.emptyView.buttonText"
                    onButtonClick={onCreate}
                    icon={ICONS.RESOURCE}
                />
            }
            data-cy={FAILOVER_DOMAINS.TABLE}
            columns={columns}
            data={data}
            isLoading={isLoading}
        />
    );
};

export default FailoverDomainTable;
